import React, { useContext } from 'react';
import styled from 'styled-components';

import Icon from '../../misc/Icon';
import Button from '../../misc/Button';
import InputSettingsField from './InputSettingsField';
import { getFieldInputType, getDisplayValue, getChangeValue } from '../fields/TextField';
import { TableColumnLogicCondition, CELL_LOGIC_COLUMN_TYPES } from './CellVisibilityLogicSettingsForm';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import ConditionalLogic from '../../../constants/ConditionalLogic';
import FieldTypes from '../../../constants/FieldTypes';

import { sortFieldPositionsByXAndYCoordinates } from '../../../lib/documentHelper';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledStepLabel = styled.div`
  font-weight: 500;
  margin-left: 6px;
  font-size: 14px;
  color: ${(props) => props.theme.fontColorPrimary};
`;

const StyledSectionLabel = styled.div`
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 6px;
  color: ${(props) => props.theme.fontColorSecondary};
`;

const StyledConditionColumn = styled.div`
  display: flex;
  flex-direction: column;
  ${(props) => (props.conditions && props.conditions.length > 1 ? 'max-height: 450px; overflow-y: auto;' : '')};
`;

const StyledConditionRow = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 8px;
  flex-direction: row;
  align-items: center;
`;

export const RequiredLogicSettingsForm = ({
  fileId,
  targetLabel,
  fields,
  sortedPages,
  targetPageId,
  targetFieldId,
  tableColumns,
  targetColumnId,
  schemaId,
  logic,
  onChange
}) => {

  const theme = useContext(ThemeContext);

  const formattedSortedPages = sortedPages
    //Step 1: Add easily identifable page label for selector
    .map((page, i) => ({ ...page, label: `Page ${i + 1}` }))
    //Step 2: Remove currently active page. We don't allow selection of conditions from own page.
    .filter((page) => page._id !== targetPageId);

  const fieldLookup = {};
  fields.forEach((field) => fieldLookup[field._id] = field);

  /**
   * Column conditions are only offered when this form is mounted for a table cell (the Cell
   * Required Logic mount passes `tableColumns`). Field-level / column-level required logic omit
   * `tableColumns` and therefore render exactly as before (page conditions only).
   */
  const showColumnSection = Array.isArray(tableColumns);

  const eligibleColumns = (tableColumns || []).filter((col) => (
    col._id !== targetColumnId
    && !col.deleted
    && CELL_LOGIC_COLUMN_TYPES.includes(col.type)
  ));

  //Split the flat conditions[] array: page conditions carry `file`, column conditions do not.
  const indexedConditions = (logic?.conditions || []).map((c, i) => ({ ...c, _originalIndex: i }));
  const pageConditions = indexedConditions.filter((c) => c.file !== undefined);
  const columnConditions = indexedConditions.filter((c) => c.file === undefined);

  const handleChange = (key, value) => {

    const nextLogic = JSON.parse(JSON.stringify(logic));

    nextLogic[key] = value;

    if (onChange) onChange(nextLogic);

  };

  const handleConditionChange = (index, changes) => {

    const nextLogic = JSON.parse(JSON.stringify(logic));

    if (!nextLogic.conditions[index]) return;

    nextLogic.conditions[index] = {
      ...nextLogic.conditions[index],
      ...changes
    };

    onChange(nextLogic);

  };

  const handlePageConditionCreate = () => {

    const nextLogic = JSON.parse(JSON.stringify(logic));

    if (nextLogic.conditions) {
      nextLogic.conditions.push({ file: fileId });
    } else {
      nextLogic.conditions = [{ file: fileId }];
    }

    onChange(nextLogic);

  };

  const handleColumnConditionCreate = () => {

    const nextLogic = JSON.parse(JSON.stringify(logic));

    //Collections keep a `schema` reference so column renames stay in sync; plain tables omit it.
    const newCondition = schemaId ? { schema: schemaId, column: null } : { column: null };

    if (nextLogic.conditions) {
      nextLogic.conditions.push(newCondition);
    } else {
      nextLogic.conditions = [newCondition];
    }

    onChange(nextLogic);

  };

  const handleConditionDelete = (index) => {

    const nextLogic = JSON.parse(JSON.stringify(logic));

    nextLogic.conditions.splice(index, 1);

    onChange(nextLogic);

  };

  return (
    <StyledWrapper>
      <StyledConditionRow>
        <div style={{ width: '95px' }}>
          <InputSettingsField
            type="dropdown"
            value={logic.action}
            options={[
              { _id: ConditionalLogic.requiredActions.enforce, value: 'Enforce' },
              { _id: ConditionalLogic.requiredActions.unenforce, value: 'Unenforce' },
            ]}
            onChange={(i, value) => handleChange('action', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>
          this
          {' '}
          {targetLabel}
          {' '}
          when
        </StyledStepLabel>
      </StyledConditionRow>
      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            inputStyles={{ maxWidth: '100px' }}
            value={logic.eval}
            options={[
              { _id: 'and', value: 'All' },
              { _id: 'or', value: 'Any' },
            ]}
            onChange={(i, value) => handleChange('eval', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>of the following conditions match</StyledStepLabel>
      </StyledConditionRow>
      {showColumnSection && (
        <>
          <StyledSectionLabel theme={theme}>Column</StyledSectionLabel>
          <StyledConditionRow style={{ marginBottom: 0 }}>
            <StyledConditionColumn style={{ flex: 1 }} conditions={columnConditions}>
              {columnConditions.map((condition) => (
                <TableColumnLogicCondition
                  key={condition._originalIndex}
                  index={condition._originalIndex}
                  columns={eligibleColumns}
                  column={condition.column}
                  condition={condition.condition}
                  value={condition.value}
                  onChange={handleConditionChange}
                  onDelete={handleConditionDelete}
                />
              ))}
            </StyledConditionColumn>
          </StyledConditionRow>
          <StyledConditionRow>
            <Button icon={ThemeIcons.misc.add} onClick={handleColumnConditionCreate} />
          </StyledConditionRow>
          <StyledSectionLabel theme={theme}>Page</StyledSectionLabel>
        </>
      )}
      <StyledConditionRow style={{ marginBottom: 0 }}>
        <StyledConditionColumn style={{ flex: 1 }} conditions={pageConditions}>
          {pageConditions.map((condition) => (
            <RequiredLogicCondition
              key={condition._originalIndex}
              index={condition._originalIndex}
              fieldLookup={fieldLookup}
              pages={formattedSortedPages}
              targetFieldId={targetFieldId}
              {...condition}
              onChange={handleConditionChange}
              onDelete={handleConditionDelete}
            />
          ))}
        </StyledConditionColumn>
      </StyledConditionRow>
      <StyledConditionRow>
        <Button
          icon={ThemeIcons.misc.add}
          onClick={handlePageConditionCreate}
        />
      </StyledConditionRow>
    </StyledWrapper>
  );

};

const StyledConditionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  padding: 8px;
  margin-bottom: 8px;
  padding-bottom: 2px;
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background-color: ${(props) => props.theme.secondaryColor};
`;

const StyledValueNodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 4px;
`;

const StyledConditionSelectWrapper = styled.div`
  display: flex;
`;

const StyledConditionDelete = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
`;

export const RequiredLogicCondition = ({

  //Lookup options
  fieldLookup, //All fields from doc.fields
  pages, //Pages sorted by pageOrder
  targetFieldId,

  //Condition Properties
  index,
  file,
  page,
  field,
  condition,
  value,
  onChange,
  onDelete,

}) => {

  const theme = useContext(ThemeContext);

  const handleChange = (key, val) => {

    const nextForm = { file, page, field, condition, value, [key]: val };

    if (key === 'page') {
      delete nextForm.field;
      delete nextForm.value;
    } else if (key === 'field') {
      delete nextForm.value;
    }

    if (onChange) onChange(index, nextForm);

  };

  const selectedPage = !page ? null : pages.find((p) => p._id === page);

  const selectedPageFieldsLookup = {};
  const selectedPageFields = !selectedPage ? null : [];

  if (selectedPage) {

    const sortedFieldPositions = sortFieldPositionsByXAndYCoordinates(selectedPage.fieldPositions);

    sortedFieldPositions.forEach((fieldPosition) => {

      /**
       * 1. Remove currently active field. We don't allow selection of conditions from own field.
       * 2. Ignore any unsupported field types
       */
      if (fieldPosition.field === targetFieldId) return;

      if (
        fieldPosition.type !== FieldTypes.text
        && fieldPosition.type !== FieldTypes.textarea
        && fieldPosition.type !== FieldTypes.number
        && fieldPosition.type !== FieldTypes.dropdown
        && fieldPosition.type !== FieldTypes.multiSelect
      ) return;

      /**
       * Only add fields to the page field list if they haven't already been added.
       * We do this because multiple page fieldPositions could link to the same field.
       */
      if (!selectedPageFieldsLookup[fieldPosition.field]) {
        selectedPageFields.push(fieldLookup[fieldPosition.field]);
        selectedPageFieldsLookup[fieldPosition.field] = true;
      }

    });
  }

  const selectedField = !field ? null : fieldLookup[field];

  const pageOptions = pages?.map((page) => {
    return { _id: page._id, value: page.label };
  });

  const fieldOptions = selectedPageFields?.map((field) => {
    return { _id: field._id, value: field.title || field.identifier || field.type };
  });

  let conditionOptions = [];
  let valueNode = null;

  if (
    selectedField &&
    (selectedField.type === FieldTypes.multiSelect || selectedField.type === FieldTypes.dropdown)
  ) {

    conditionOptions = [
      { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
      { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
      { _id: ConditionalLogic.conditions.equal, value: 'is' },
      { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
    ];

    const fieldOptions = [];
    if (selectedField && selectedField.options) {
      selectedField.options.forEach((opt) => {
        if (!opt.deleted) fieldOptions.push({ _id: opt._id, value: opt.value });
      });
    }

    valueNode = (
      <InputSettingsField
        value={value}
        type="dropdown"
        options={fieldOptions}
        onChange={(i, value) => handleChange('value', value)}
      />
    );

  } else if (
    selectedField
    && (selectedField.type === FieldTypes.number)
  ) {

    conditionOptions = [
      { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
      { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
      { _id: ConditionalLogic.conditions.equal, value: 'is' },
      { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
      { _id: ConditionalLogic.conditions.greaterThan, value: 'is greater than' },
      { _id: ConditionalLogic.conditions.lessThan, value: 'is less than' },

    ];

    valueNode = (
      <InputSettingsField
        type={getFieldInputType(false, selectedField.type, selectedField.format)}
        value={getDisplayValue(selectedField.type, value, selectedField.format)}
        onChange={(i, value) => handleChange('value', getChangeValue(selectedField.type, value))}
      />
    );

  } else if (
    selectedField
    && (selectedField.type === FieldTypes.text || selectedField.type === FieldTypes.textarea)
  ) {

    conditionOptions = [
      { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
      { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
      { _id: ConditionalLogic.conditions.equal, value: 'is' },
      { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
      { _id: ConditionalLogic.conditions.contain, value: 'contains' },
    ];

    valueNode = (
      <InputSettingsField
        type={getFieldInputType(false, selectedField.type, selectedField.format)}
        value={getDisplayValue(selectedField.type, value, selectedField.format)}
        onChange={(i, value) => handleChange('value', getChangeValue(selectedField.type, value))}
      />
    );

  }

  return (
    <StyledConditionWrapper theme={theme}>
      <StyledConditionColumn style={{ flex: 1 }}>
        <StyledConditionRow>
          <InputSettingsField
            label="Page"
            value={page}
            type="dropdown"
            options={pageOptions}
            onChange={(i, pageId) => handleChange('page', pageId)}
          />
        </StyledConditionRow>
        {!selectedPage ? null : (
          <StyledConditionRow>
            <InputSettingsField
              label="Field"
              value={field}
              type="dropdown"
              options={fieldOptions}
              onChange={(i, value) => handleChange('field', value)}
            />
          </StyledConditionRow>
        )}
        {!selectedPage || !selectedField ? null : (
          <StyledConditionRow>
            <StyledConditionSelectWrapper>
              <InputSettingsField
                value={condition}
                type="dropdown"
                options={conditionOptions}
                onChange={(i, value) => handleChange('condition', value)}
              />
            </StyledConditionSelectWrapper>
            <StyledValueNodeWrapper>
              {condition === ConditionalLogic.conditions.empty || condition === ConditionalLogic.conditions.filled ? null : valueNode}
            </StyledValueNodeWrapper>
          </StyledConditionRow>
        )}
      </StyledConditionColumn>
      <StyledConditionColumn>
        <StyledConditionDelete>
          <Icon
            color={theme.dangerColor}
            icon={ThemeIcons.settings.delete}
            size="sm"
            onClick={() => onDelete(index)}
          />
        </StyledConditionDelete>
      </StyledConditionColumn>
    </StyledConditionWrapper>
  );
};

export default RequiredLogicSettingsForm;
